import React from "react";
import { NavLink } from "react-router-dom";
import Owner from "../components/common/Owner";
import { Radio, Flex, Typography } from "antd";
import { SafetyCertificateTwoTone } from "@ant-design/icons";
import { GroupSyncTypeNames } from "./enums";
import GroupMembershipComment from "../components/common/GroupMembershipComment";
import TooltipIcon from "../components/common/TooltipIcon";

export const privacyTypes = Object.freeze({
  Members: "Members",
  Open: "Open",
  Admins: "Admins",
});

export const selfSubscriptionTypes = Object.freeze({
  Closed: "Closed",
  Open: "Open",
  CernUsers: "CernUsers",
});

export const resourceCategories = Object.freeze({
  Undefined: "Undefined",
  Official: "Official",
  Personal: "Personal",
  Test: "Test",
});

export const getMemberTypeLabel = (record, isMailSender = false) => {
  if (record.memberGroup) {
    return "Group";
  }

  const memberIdentity = record.memberIdentity;
  const memberType = memberIdentity?.type;
  const hasSource = memberIdentity?.source;

  if (memberIdentity && !memberIdentity.upn) {
    return isMailSender ? "Mail Sender" : "Mail recipient";
  }

  if (hasSource && hasSource.toLowerCase() !== "cern") {
    return "External";
  }

  return memberType || "Unknown";
};

export const renderMemberIdentifier = (record) => {
  if (record.memberGroup) {
    return (
      <Flex
        align="center"
        style={{
          minHeight: 40,
        }}
      >
        <NavLink to={`/group/${record.memberGroup.groupIdentifier}/details`}>
          {record.memberGroup.displayName}
        </NavLink>
      </Flex>
    );
  }

  const identity = record.memberIdentity;
  const email = record.emailAddress || identity?.email;

  let identifierContent;
  if (identity?.displayName && (identity?.upn || identity?.unconfirmed)) {
    const memberType = identity?.type;
    const isUnconfirmed = identity?.unconfirmed;
    const personId = identity?.personId;

    if (isUnconfirmed) {
      identifierContent = identity.displayName;
    } else {
      const source = identity?.source;
      if (source && source.toLowerCase() !== "cern") {
        identifierContent = identity.upn
          ? `${identity.displayName} - ${source} (${identity.upn})`
          : identity.displayName;
      } else {
        const isMemberTypePerson = memberType === "Person" && personId;

        // Build organizational unit string (department-group-section)
        const orgUnitParts = [];
        if (identity.cernDepartment) orgUnitParts.push(identity.cernDepartment);
        if (identity.cernGroup) orgUnitParts.push(identity.cernGroup);
        if (identity.cernSection) orgUnitParts.push(identity.cernSection);
        const orgUnit = orgUnitParts.length > 0 ? orgUnitParts.join("-") : null;

        identifierContent = (
          <span>
            {identity.displayName}
            {isMemberTypePerson && orgUnit && (
              <span style={{ color: "rgba(0, 0, 0, 0.65)" }}> {orgUnit}</span>
            )}{" "}
            ({identity.upn || "pending confirmation"})
            {isMemberTypePerson && (
              <TooltipIcon
                title="Verified CERN user"
                icon={<SafetyCertificateTwoTone />}
                color="#52c41a"
              />
            )}
          </span>
        );
      }
    }
  }

  return (
    <Flex
      vertical
      justify="center"
      style={{
        minHeight: 40,
      }}
    >
      <Typography.Text>{identifierContent}</Typography.Text>
      {email && (
        <Typography.Text
          type="secondary"
          style={{
            fontSize: 12,
            lineHeight: "1.5",
          }}
        >
          {email}{" "}
          {record.emailAddress && (
            <TooltipIcon
              title={`Any communications will be sent to ${record.emailAddress} rather than ${identity.email}. To change this please add the member again with the desired email and click to change mailing address.`}
            />
          )}
        </Typography.Text>
      )}
    </Flex>
  );
};

export const defaultGroupTableColumns = [
  {
    title: "Name",
    key: "groupIdentifier",
    dataIndex: "groupIdentifier",
    render: (text, group) => (
      <NavLink to={`/group/${group.groupIdentifier}/details`}>{text}</NavLink>
    ),
  },
  {
    title: "Type",
    dataIndex: "dynamic",
    render: (value) => (value ? <span>Dynamic</span> : <span>Static</span>),
  },
  {
    title: "Sync Type",
    dataIndex: "syncType",
    render: (syncType) => GroupSyncTypeNames[syncType],
  },
  {
    title: "Description",
    dataIndex: "description",
  },
  {
    title: "Blocked Status",
    dataIndex: "blocked",
    render: (blocked) =>
      blocked ? <span>Blocked</span> : <span>Enabled</span>,
  },
  {
    title: "Owner",
    dataIndex: "owner",
    render: (owner) => (owner ? <Owner owner={owner} /> : <span>None</span>),
  },
];

export const externalGroupTableColumns = [
  {
    title: "Name",
    key: "groupIdentifier",
    dataIndex: "groupIdentifier",
    render: (text, group) => (
      <NavLink to={`/group/${group.groupIdentifier}/details`}>{text}</NavLink>
    ),
  },
  {
    title: "Description",
    dataIndex: "description",
  },
  {
    title: "Display Name",
    dataIndex: "displayName",
  },
];

export const getDefaultGroupMembersTableColumns = (
  selectedGroupId,
  disabled
) => [
  {
    title: "Identifier",
    key: "upn" || "groupIdentifier",
    dataIndex: "displayName" || "groupIdentifier",
    render: (_, record) => renderMemberIdentifier(record),
  },
  {
    title: "Member type",
    dataIndex: "type",
    render: (_, record) => getMemberTypeLabel(record),
  },
  {
    title: "Comment",
    dataIndex: "comment",
    render: (_, record) => {
      return (
        <GroupMembershipComment
          record={record}
          selectedGroupId={selectedGroupId}
          disabled={disabled}
        />
      );
    },
  },
];

export const SelfSubscriptionRadioGroup = React.forwardRef((props, ref) => {
  const isDisabled = props.disabled;

  return (
    <Radio.Group
      ref={ref}
      disabled={isDisabled}
      {...props}
      defaultValue={selfSubscriptionTypes.Closed}
    >
      <Radio {...props} value={selfSubscriptionTypes.Closed}>
        Closed{" "}
        <TooltipIcon title="Self-Subscription is closed. Group membership is managed by the group administrators." />
      </Radio>
      <Radio {...props} value={selfSubscriptionTypes.Open}>
        Open{" "}
        <TooltipIcon title="Self-Subscription is available to anyone with or without a CERN account." />
      </Radio>
      <Radio {...props} value={selfSubscriptionTypes.CernUsers}>
        CERN Users{" "}
        <TooltipIcon title="Self-Subscription is available to people with a CERN account. Other members can be added by the group administrators." />
      </Radio>
    </Radio.Group>
  );
});

export const PrivacyRadioGroup = React.forwardRef((props, ref) => {
  const { disabled, defaultValue, ...restProps } = props;

  return (
    <Radio.Group
      ref={ref}
      disabled={disabled}
      defaultValue={defaultValue}
      {...restProps}
    >
      <Radio {...restProps} value={privacyTypes.Admins}>
        Group Administrators
      </Radio>
      <Radio {...restProps} value={privacyTypes.Members}>
        Group Members
      </Radio>
      <Radio {...restProps} value={privacyTypes.Open}>
        CERN Users
      </Radio>
    </Radio.Group>
  );
});

export const ResourceCategoryRadioGroup = React.forwardRef((props, ref) => {
  const isDisabled = props.disabled;

  return (
    <Radio.Group
      ref={ref}
      disabled={isDisabled}
      {...props}
      defaultValue={resourceCategories.Test}
    >
      <Radio value={resourceCategories.Test}>Test</Radio>
      <Radio value={resourceCategories.Official}>Official</Radio>
      <Radio value={resourceCategories.Personal}>Personal</Radio>
    </Radio.Group>
  );
});

export const DEFAULT_GROUP_MEMBERS_TABLE_QUERY_FIELDS = [
  ...getDefaultGroupMembersTableColumns(null).map((i) => i.dataIndex),
  "unconfirmed",
  "unconfirmedEmail",
];

export const DEFAULT_GROUP_TABLE_QUERY_FIELDS = defaultGroupTableColumns.map(
  (i) => i.dataIndex
);

export const searchMaxLimit = 1000;

export const identityMaxSearchLimit = 100;
